// opencv doesn't have a type definition currently
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const cv: any;

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * This function attempts to find a better place to add a form field
 * given a pdf canvas and a selection in that canvas.
 * Image processing through opencv is used to find boundaries in the selection
 * and a rectangle is drawn that attempts to avoid those boundaries.
 *
 * @param canvas the canvas from pdfjs that the selection was done on
 * @param canvasXScale the x scale of the canvas' coordinate system compared to the selection
 * @param canvasYScale the y scale of the canvas' coordinate system compared to the selection
 * @param startX an x coordinate from the selection
 * @param startY a y coordinate from the selection
 * @param endX the other x coordinate from the selection
 * @param endY the other y coordinate from the selection
 * @return a rectangle within the selection, or undefined if there was an error
 */
export function getBestSelectionRectangle({
  canvas,
  canvasXScale,
  canvasYScale,
  startX,
  startY,
  endX,
  endY,
}: {
  canvas: HTMLCanvasElement;
  canvasXScale: number;
  canvasYScale: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}) {
  const selectionWidth = Math.abs(endX - startX) * canvasXScale;
  const selectionHeight = Math.abs(endY - startY) * canvasYScale;
  const selectionCenterX = selectionWidth / 2;
  const selectionCenterY = selectionHeight / 2;

  // a non 0 area is needed for processing
  if (selectionHeight === 0 || selectionWidth === 0) return undefined;

  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  // get a crop of the current selection from the canvas
  const imgData = ctx.getImageData(
    Math.min(startX, endX) * canvasXScale,
    Math.min(startY, endY) * canvasYScale,
    selectionWidth,
    selectionHeight,
  );

  // use a temporary canvas to save the crop in
  const outCanvas = document.querySelector<HTMLCanvasElement>('#draftCanvas');
  if (!outCanvas) return undefined;

  const ctx2 = outCanvas.getContext('2d');
  if (!ctx2) return undefined;

  ctx2.clearRect(0, 0, outCanvas.width, outCanvas.height);
  outCanvas.width = imgData.width;
  outCanvas.height = imgData.height;
  ctx2.putImageData(imgData, 0, 0);

  // read the crop from the temporary canvas into an opencv matrix
  const src = cv.imread('draftCanvas');

  // convert to gray ccale
  const grayScaleImage = new cv.Mat(src.rows, src.cols, cv.CV_8UC1);
  cv.cvtColor(src, grayScaleImage, cv.COLOR_RGB2GRAY);

  // convert to binary using a threshold
  const binaryNoBorder = new cv.Mat(src.rows, src.cols, cv.CV_8UC1);
  cv.threshold(grayScaleImage, binaryNoBorder, 180, 255, cv.THRESH_BINARY);

  // add a white 1px border around the binary image to work around https://stackoverflow.com/questions/20418416/how-to-fill-contours-that-touch-the-image-border
  const binary = new cv.Mat();
  const borderOffset = 1;
  cv.copyMakeBorder(
    binaryNoBorder,
    binary,
    borderOffset,
    borderOffset,
    borderOffset,
    borderOffset,
    cv.BORDER_CONSTANT,
    new cv.Scalar(255, 255, 255, 255),
  );

  // find contours in the binary image
  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();
  cv.findContours(
    binary,
    contours,
    hierarchy,
    cv.RETR_LIST,
    cv.CHAIN_APPROX_SIMPLE,
  );

  // process all thr found contours
  const rects = [];
  const objectsToDelete = [];
  for (let i = 0; i < contours.size(); i += 1) {
    const contour = contours.get(i);
    objectsToDelete.push(contour);

    const area = cv.contourArea(contour);
    // skip points
    // eslint-disable-next-line no-continue
    if (area === 0) continue;

    const { x, y, width: cwidth, height: cheight } = cv.boundingRect(contour);

    // skip a contour if it's just the whole canvas
    if (
      x === 0 &&
      y === 0 &&
      cwidth === selectionWidth &&
      cheight === selectionHeight
    ) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // save the contour bounding box and points to process later
    const points = [];
    for (let j = 0; j < contour.rows; j += 1) {
      points.push({
        x: contour.data32S[j * 2],
        y: contour.data32S[j * 2 + 1],
      });
    }
    rects.push({ x, y, width: cwidth, height: cheight, points });
  }

  // try to see if any long contours exist on the top or bottom of the selection (special case for field borders)
  let topLine: Rect | undefined;
  let bottomLine: Rect | undefined;
  rects.forEach(rect => {
    if (rect.width > selectionWidth / 2) {
      if (rect.y + rect.height < selectionCenterY) {
        if (!topLine || topLine.y < rect.y) {
          topLine = rect;
        }
      } else if (rect.y > selectionCenterY) {
        if (!bottomLine || bottomLine.y > rect.y) {
          bottomLine = rect;
        }
      }
    }
  });

  // default selection
  let topBound = 0;
  let bottomBound = selectionHeight;
  let leftBound = 0;
  let rightBound = selectionWidth;

  // use any found top border line as the top bound
  if (topLine) {
    topBound = topLine.y + topLine.height;
  }
  // use any found bottom border line as the bottom bound
  // and bind the inital left/right bounds to it (special case for field underlines)
  if (bottomLine) {
    bottomBound = bottomLine.y;
    leftBound = bottomLine.x;
    rightBound = bottomLine.x + bottomLine.width;
  }

  // try to resolve the top/bottom bounds from the contours found in the selection
  rects.forEach(({ points }) =>
    points.forEach(point => {
      if (!bottomLine && point.y > selectionCenterY) {
        bottomBound = Math.min(bottomBound, point.y);
      }
      if (!topLine && point.y < selectionCenterY) {
        topBound = Math.max(topBound, point.y);
      }
    }),
  );

  // try to resolve the left/right bounds from the contours found in the selection
  // between the top/bottom bounds or is nearly as big/bigger than the current bounds (like a border)
  rects.forEach(rect =>
    rect.points.forEach(point => {
      const betweenTopBottom = point.y > topBound && point.y < bottomBound;
      const intersectsTopBottom =
        rect.y <= bottomBound && rect.y + rect.height >= topBound;
      const matchesHeight = rect.height > (bottomBound - topBound) * 0.9;
      if (betweenTopBottom || (intersectsTopBottom && matchesHeight)) {
        if (point.x < selectionCenterX) {
          leftBound = Math.max(leftBound, point.x);
        }
        if (point.x > selectionCenterX) {
          rightBound = Math.min(rightBound, point.x);
        }
      }
    }),
  );

  // output the result for debugging
  if (outCanvas.style.display !== 'none') {
    ctx2.clearRect(0, 0, outCanvas.width, outCanvas.height);
    cv.imshow('draftCanvas', src);

    ctx2.fillStyle = '#0000aa44';
    ctx2.fillRect(
      leftBound - borderOffset,
      topBound - borderOffset,
      rightBound - leftBound,
      bottomBound - topBound,
    );

    ctx2.fillStyle = '#ff0000';
    rects.forEach(({ points }) =>
      points.forEach(({ x, y }) => {
        // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
        ctx2!.fillRect(x - borderOffset, y - borderOffset, 2, 2);
      }),
    );
  }

  // cleanup created objects
  objectsToDelete.forEach(obj => obj.delete());
  hierarchy.delete();
  contours.delete();
  binary.delete();
  binaryNoBorder.delete();
  grayScaleImage.delete();
  src.delete();

  return {
    x: (leftBound - borderOffset) / canvasXScale,
    y: (topBound - borderOffset) / canvasYScale,
    width: (rightBound - leftBound) / canvasXScale,
    height: (bottomBound - topBound) / canvasYScale,
  };
}
